<div *ngIf="isUpdating" class="updating-overlay"></div>

<div class="table-container" #tableContainer>
  <div class="list-header">
    <div class="frozen-columns">
      <ng-template *ngTemplateOutlet="headerSelectionTemplate"></ng-template>
      <div class="item-header-cell">Item</div>
    </div>
    <cc-tool-tip contentKey="listItemColourExplainer">
      <div class="list-header-property list-style-property">Colour</div>
    </cc-tool-tip>
    <ng-container *ngIf="list?.properties">
      <cc-list-header-property
        *ngFor="
          let _ of beforeAddPropertyIterator;
          index as index;
          trackBy: propertyTrackByBeforeAdd.bind(this)
        "
        [index]="index"
        [isLast]="false"
        (onPropertyMoved)="onPropertyMoved($event)"
        (onRequestAddProperty)="startAddPropertyInBetween($event)"
        [property]="list.properties[index]"
        [editable]="!isDeployedList"
        [users]="users"
        (propertyChanged)="onListHeaderPropertyChanged($event)"
        (propertyDeleted)="onListPropertyDeleted($event)"
      ></cc-list-header-property>

      <cc-list-property-add
        *ngIf="addPropertyIndex !== null"
        (propertyCreated)="onListPropertyAddedInBetween($event)"
        (propertyCancelled)="cancelAddPropertyInBetween()"
        data-test="add-property-in-between"
        #addListPropertyInBetween
        [list]="list"
        [parentId]="parentId"
        [hierarchy]="folder?.hierarchy"
        [addIndex]="addPropertyIndex"
      >
      </cc-list-property-add>

      <cc-list-header-property
        *ngFor="
          let _ of afterAddPropertyIterator;
          index as index;
          last as last;
          trackBy: propertyTrackByAfterAdd.bind(this)
        "
        [index]="index + addPropertyIndex"
        [isLast]="last"
        (onPropertyMoved)="onPropertyMoved($event)"
        (onRequestAddProperty)="startAddPropertyInBetween($event)"
        [property]="list.properties[index + addPropertyIndex]"
        [editable]="!isDeployedList"
        [users]="users"
        (propertyChanged)="onListHeaderPropertyChanged($event)"
        (propertyDeleted)="onListPropertyDeleted($event)"
      ></cc-list-header-property>
    </ng-container>

    <cc-list-property-add
      *ngIf="list && !list.deployedParents?.includes(parentId)"
      (propertyCreated)="onListPropertyUpdated($event)"
      [list]="list"
      [parentId]="parentId"
      [hierarchy]="folder?.hierarchy"
      #addListProperty
    >
    </cc-list-property-add>
  </div>

  <ng-container *ngIf="list">
    <div
      *ngFor="let item of list?.items; i as index"
      class="list-item"
      data-test="list-item-non-static"
      [class.new]="item.editing === true && item.isNew === true"
      [class.expanded]="expandedRow === item._id"
    >
      <div class="frozen-columns">
        <div class="list-item-action-cell">
          <button
            *ngIf="item.isNew !== true"
            type="button"
            class="list-selection-checkbox"
            aria-role="checkbox"
            data-test="select-list-item"
            (click)="toggleItemSelection(item._id)"
            [attr.aria-checked]="(selectedItems | async).has(item._id)"
            aria-label="Select this item"
          >
            <i class="cc-icon cc-icon-check cc-icon-sm" aria-hidden="true"></i>
          </button>
          <!-- Note that the *button* below is aria-hidden, since extra screen space isn't relevant for screen readers -->
          <!-- DON'T follow this pattern without a good reason. use the above (aria-hidden on the icon only) -->
          <button
            type="button"
            (click)="toggleRowExpanded(item._id)"
            class="expand-row-button"
            aria-hidden="true"
            *ngIf="item.isNew !== true"
          >
            <em class="cc-icon cc-icon-arrow-right-small"></em>
          </button>
        </div>
        <div
          class="list-item-property list-item-name-container"
          [class.editing]="item.editing === true"
          [class.new]="item.isNew === true"
        >
          <ng-container *ngIf="item.editing === true">
            <input
              type="text"
              class="list-item-name-input"
              [value]="item.value"
              *ngIf="!isDeployedList"
              (blur)="onListItemEdited(item, $event.target.value)"
              (keyup.esc)="onListItemEditCancelled(item)"
              (keyup.enter)="onListItemEdited(item, $event.target.value, true)"
              placeholder="Type item name..."
              ccFocus
            />
            <cc-list-typeahead
              class="sm-w-full"
              [list]="list"
              [parentId]="parentId"
              (listDeployed)="onListDeployed($event)"
              (cancel)="onListItemEditCancelled(item)"
              *ngIf="isDeployedList"
            ></cc-list-typeahead>
          </ng-container>
          <ng-container *ngIf="item.editing !== true">
            <div
              *ngIf="!isDeployedList"
              (click)="setItemEditMode(item, true)"
              class="list-item-name-display non-deployed"
              data-test="edit-item-name"
            >
              {{ item.value }}
            </div>
            <div *ngIf="isDeployedList" class="list-item-name-display deployed">
              {{ item.value }}
            </div>
          </ng-container>
        </div>
      </div>
      <div
        class="list-item-property list-style-property"
        [class.editing]="item.editing === true"
        [class.new]="item.isNew === true"
      >
        <cc-style-color-picker
          [(style)]="item.style"
          (styleChange)="onListItemStyleUpdated($event, item)"
          [defaultStyle]="styleVariants.None"
          [disabled]="isDeployedList"
        ></cc-style-color-picker>
      </div>
      <ng-container *ngIf="item.isNew !== true && list?.properties">
        <cc-list-property
          *ngFor="
            let _ of beforeAddPropertyIterator;
            index as index;
            trackBy: propertyTrackByBeforeAdd.bind(this)
          "
          [item]="item"
          [editable]="!isDeployedList"
          [listItemPropertyMap]="listItemPropertyMap"
          [propertyHeader]="list.properties[index]"
          (propertyUpdated)="onListCellUpdated($event, item, index)"
          [companyId]="folder?.company?.id"
          [projectId]="folder?.project?.id"
          [listId]="list.id"
          (focusin)="handleExpandRow(item, list.properties[index])"
        ></cc-list-property>
        <div class="empty-list-property" *ngIf="addPropertyIndex !== null"></div>
        <cc-list-property
          *ngFor="
            let _ of afterAddPropertyIterator;
            index as index;
            trackBy: propertyTrackByAfterAdd.bind(this)
          "
          [item]="item"
          [editable]="!list.deployedParents?.includes(parentId)"
          [listItemPropertyMap]="listItemPropertyMap"
          [propertyHeader]="list.properties[index + addPropertyIndex]"
          (propertyUpdated)="onListCellUpdated($event, item, index + addPropertyIndex)"
          [companyId]="folder?.company?.id"
          [projectId]="folder?.project?.id"
          [listId]="list.id"
          (focusin)="handleExpandRow(item, list.properties[index + addPropertyIndex])"
        ></cc-list-property>
      </ng-container>
    </div>
  </ng-container>

  <div
    class="list-item sm-flex-1"
    [class.expand-list-item-row]="list?.properties?.length > 0"
    *ngIf="!isCreatingNewListItem"
  >
    <div class="frozen-columns">
      <div class="list-item-action-cell"></div>
      <div *ngIf="list" class="add-list-item-container">
        <button [disabled]="!canAddNewItem" class="add-item-button" type="button" (click)="startAddItem()">
          <em class="cc-icon cc-icon-plus" aria-hidden="true"></em>
          <span>Add item</span>
        </button>
      </div>
    </div>
    <div *ngIf="list" class="list-default-style-container">
      <cc-style-color-picker
        [(style)]="list.defaultStyle"
        (styleChange)="onListDefaultStyleUpdated($event)"
        [defaultStyle]="styleVariants.None"
      ></cc-style-color-picker>
    </div>
  </div>
</div>

<cc-empty-list-state
  *ngIf="shouldDisplayEmptyState"
  [hasItemsButNoProperty]="hasItems && !hasProperties"
  (addProperty)="onAddProperty()"
  (startAddItem)="startAddItem()"
  (importCSV)="triggerUploadListCSV.emit()"
></cc-empty-list-state>

<ng-template #headerSelectionTemplate>
  <div class="list-item-action-cell list-header-cell">
    <cc-popover placement="right">
      <span content>
        {{
          list?.items?.length && list.items.length === (selectedItems | async).size
            ? 'Deselect All'
            : 'Select All'
        }}
      </span>
      <button
        trigger
        type="button"
        class="list-selection-checkbox"
        aria-role="checkbox"
        data-test="select-all-items"
        (click)="toggleAllItemsSelection()"
        [disabled]="list?.items?.length === 0"
        [attr.aria-checked]="list?.items?.length && list.items.length === (selectedItems | async).size"
      >
        <i class="cc-icon cc-icon-check cc-icon-sm" aria-hidden="true"></i>
      </button>
    </cc-popover>
  </div>
</ng-template>
